import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { TabsLayout, StatCard, PageNavigation, PageBackground } from '../shared';
import { Table } from '../shared/table';
import { 
  GroupOverview,  
  EventCard, 
  RecentActivities,
  getGroupInfoColumns, 
  ParticipantToolbar, 
  BulkActionsBar,
  SendbirdMessaging,
  GroupNeeds,
  GroupFiles
} from './components';
import { useGroupDetailData, useMemberSelection, useParticipantTableConfig } from './hooks';
import { GroupMember } from '../../types/groupDetail';

export function GroupDetail() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get group detail data from route params
  const { groupDetail, sortedMembers, loading, loadingMore, error } = useGroupDetailData();
  
  const memberSelection = useMemberSelection(sortedMembers, searchQuery);
  const {
    selectedMembers,
    filteredMembers,
    handleSelectAll,
    handleSelectMember,
    clearSelection,
    handleBulkRoleChange,
    handleBulkRemove,
    isAllSelected,
    isMemberSelected,
  } = memberSelection;

  // Table columns hook
  const participantColumns = useParticipantTableConfig({
    isAllSelected,
    handleSelectAll,
    isMemberSelected,
    handleSelectMember,
  });

  // Loading state with page structure
  if (loading) {
    return (
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          {/* Navigation skeleton */}
          <div className="h-6 bg-gray-200 rounded w-48 mb-6 animate-pulse"></div>
          
          {/* Group header skeleton */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-gray-200 rounded-lg animate-pulse"></div>
              <div className="flex-1 space-y-3">
                <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
              </div>
            </div>
          </div>
          
          {/* Tabs skeleton */}
          <div className="flex gap-4 border-b border-gray-200 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
            ))}
          </div>
          
          {/* Content skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="h-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </PageBackground>
    );
  }

  // Error state
  if (error) {
    return (
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center max-w-md">
              <div className="text-red-600 mb-4">
                <LucideIcons.AlertCircle className="h-12 w-12 mx-auto" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Group</h2>
              <p className="text-gray-600 mb-4">{error.message}</p>
              <button
                onClick={() => navigate('/groups')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Back to Groups
              </button>
            </div>
          </div>
        </main>
      </PageBackground>
    );
  }

  // No group found state
  if (!groupDetail) {
    return (
      <div className="w-full bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Group not found</p>
          <button
            onClick={() => navigate('/groups')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Groups
          </button>
        </div>
      </div>
    );
  }

  // Define kebab menu items
  const kebabMenuItems = [
    { label: 'View Group Calendar', onClick: () => console.log('View calendar'), icon: LucideIcons.Calendar, mobileOnly: true },
    { label: 'Send Participant Logins', onClick: () => console.log('Send Participant Logins'), icon: LucideIcons.KeyRound },
    { label: 'Record Attendance', onClick: () => console.log('Record Attendance'), icon: LucideIcons.ClipboardCheck },
    { label: 'Send SMS', onClick: () => console.log('Send SMS'), icon: LucideIcons.MessageSquare },
    { label: 'Send Mail Merge', onClick: () => console.log('Send Mail Merge'), icon: LucideIcons.Mail },
    { label: 'Print Labels / Name Tags', onClick: () => console.log('Print Labels / Name Tags'), icon: LucideIcons.Printer },
  ];

  return <PageBackground>
      <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
        {/* Page Navigation with Kebab Menu */}
        <div className="mb-6">
          <PageNavigation
            showBreadcrumbs={true}
            breadcrumbs={[
              { label: 'All Groups', onClick: () => navigate('/groups') },
              { label: groupDetail.name }
            ]}
            primaryAction={{
              label: 'View Calendar',
              icon: LucideIcons.Calendar,
              onClick: () => console.log('View calendar')
            }}
            showEdit={true}
            onEdit={() => console.log('Edit group')}
            showSettings={true}
            onSettings={() => console.log('Settings')}
            kebabMenuItems={kebabMenuItems}
          />
        </div>
        {/* Group Overview with Info Section */}
        <GroupOverview 
          groupDetail={groupDetail}
          infoColumns={getGroupInfoColumns(groupDetail)}
          loadingMore={loadingMore}
        />
        
        {/* Tabs Section */}
        <TabsLayout
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={[
            {
              id: 'dashboard',
              label: 'Dashboard',
              content: (
                <div>
                  {/* Statistics Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <StatCard 
                      title="Total members" 
                      value={loadingMore ? `${groupDetail.members.length}+` : groupDetail.members.length.toString()} 
                      trend="+12%" 
                      change="vs last month" 
                      isPositive={true} 
                    />
                    <StatCard title="Engagement" value="87%" trend="+5%" change="vs last month" isPositive={true} />
                    <StatCard title="Messages" value="47" trend="+23%" change="vs last month" isPositive={true} />
                    <StatCard title="Avg attendance" value="92%" trend="-2%" change="vs last month" isPositive={false} />
                  </div>
                  {/* Main Content Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="pace-y-6">
                      {/* Upcoming Events */}
                      <div className="bg-white rounded-lg shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                          Upcoming events
                        </h2>
                        <div className="space-y-4">
                          <EventCard name="Event Name" date="Wed, Aug 7" time="11:00–2:00pm" status="Shared" isDraft={false} />
                          <EventCard name="Event Name" date="Wed, Aug 7" time="11:00–2:00pm" status={null} isDraft={true} />
                        </div>
                      </div>
                    </div>
                    {/* Right Column */}
                    <div className="space-y-6">
                      {/* Recent Activities */}
                      <RecentActivities />
                    </div>
                  </div>
                </div>
              ),
            },
            {
              id: 'messages',
              label: 'Messages',
              content: (
                <SendbirdMessaging 
                  userId={groupDetail.leader.id}
                  profileUrl={groupDetail.leader.avatarUrl}
                  nickname={groupDetail.leader.name}
                  groupDetail={groupDetail}
                />
              ),
            },
            {
              id: 'participants',
              label: 'Participants',
              content: (
                <div>
                  {selectedMembers.length > 0 ? (
                    <BulkActionsBar
                      selectedMembers={selectedMembers}
                      onBulkRoleChange={handleBulkRoleChange}
                      onBulkRemove={handleBulkRemove}
                      onClearSelection={clearSelection}
                    />
                  ) : (
                    <ParticipantToolbar
                      onSearchChange={setSearchQuery}
                      onAddParticipant={() => console.log('Add participant')}
                    />
                  )}
                  <Table<GroupMember>
                    data={filteredMembers}
                    columns={participantColumns}
                    getRowKey={(member) => member.id}
                    usePagination={true}
                    pageSize={10}
                    loadingMore={loadingMore}
                    emptyMessage="No participants found"
                    className="mt-6"
                  />
                </div>
              ),
            },
            {
              id: 'files',
              label: 'Files',
              content: (
                <GroupFiles groupName={groupDetail.name} />
              ),
            },
            {
              id: 'needs',
              label: 'Needs',
              content: (
                <GroupNeeds groupName={groupDetail.name} />
              ),
            },
          ]}
        />
      </main>
    </PageBackground>;
}